import { ColorPicker } from "@/lib/components/ColorPicker";
import { Input } from "@/lib/components/Input";
import { Switch } from "@/lib/components/Switch";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { ElementBorderDto, SceneTypeDto } from "@repo/api/overlays";
import { IconBaselineDensityMedium } from "@tabler/icons-react";

type Props = {
  element: {
    externalId: string;
    border: ElementBorderDto | null;
  };
  sceneType: SceneTypeDto;
};

export default function ElementBorderSection({ element, sceneType }: Props) {
  const updateElement = useEditorStore((state) => state.service.updateElement);

  return (
    <>
      <div className="mt-8 mb-2 flex items-center gap-4">
        <div className="input-label m-0">Border</div>
        <Switch
          checked={element.border !== null}
          onCheckedChange={(newValue) => {
            updateElement(element.externalId, (element) => {
              if (!("border" in element)) {
                throw new Error("Border is not in element");
              }

              if (newValue) {
                element.border = {
                  width: 10,
                  color: {
                    type: "SOLID_COLOR",
                    format: "HEX",
                    color: "#000000",
                    opacity: 100,
                  },
                };
              } else {
                element.border = null;
              }
            });
          }}
        />
      </div>
      {element.border !== null && (
        <BorderControls element={element} sceneType={sceneType} />
      )}
    </>
  );
}

function BorderControls({ element }: Props) {
  const updateElement = useEditorStore((state) => state.service.updateElement);

  if (!element.border) {
    return null;
  }

  return (
    <div className="flex gap-4">
      <ColorPicker
        className="w-[296px]"
        value={element.border.color.color}
        opacity={element.border.color.opacity ?? 100}
        onChange={(color) => {
          updateElement(element.externalId, (element) => {
            if (!("border" in element)) {
              throw new Error("Border is not in element");
            }
            element.border!.color.color = color;
          });
        }}
        onOpacityChange={(opacity) => {
          updateElement(element.externalId, (element) => {
            if (!("border" in element)) {
              throw new Error("Border is not in element");
            }
            element.border!.color.opacity = opacity;
          });
        }}
      />
      <Input
        className="w-[140px]"
        containerSize="sm"
        leadingComponent={
          <IconBaselineDensityMedium
            className="text-text-secondary"
            stroke={1.5}
            size={15}
          />
        }
        value={element.border.width}
        onValidateInternalValue={(e) => {
          if (e.target.value === "") {
            return true;
          }

          const isNumber = /^\d+$/.test(e.target.value);

          if (!isNumber) {
            return false;
          }

          const value = parseInt(e.target.value);

          return value >= 0 && value <= 1000001;
        }}
        onValidate={(e) => {
          const isValid = /^\d+$/.test(e.target.value);
          return isValid;
        }}
        onChange={(e) => {
          updateElement(element.externalId, (element) => {
            if (!("border" in element)) {
              throw new Error("Border is not in element");
            }

            element.border!.width = parseInt(e.target.value);
          });
        }}
      />
    </div>
  );
}
